import React from 'react'
import {
  MetricCard,
  StatsCard,
  Divider,
  Rating,
  DemographicsSection,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { formatInteger, formatAddress } from 'utils/formatting'
import { toLegacyAddressInfo } from 'services/nursingHome'
import { GetNursingHomeQueryResponse } from 'services/nursingHome/legacyDetailTypes'
import { GoogleMapsLink } from '../utils/GoogleMapsLink'
import { BuildCompanyTypeContentOptions, CompanyTypePageContent } from './types'

/**
 * Shared nursing-home (Senior Living) content derivation used by both
 * `NewNursingHomePage` (desktop) and `MobileNursingHomePage`.
 * See {@link CompanyTypePageContent}.
 */
export function buildNursingHomePageContent(
  data: GetNursingHomeQueryResponse | undefined,
  { onViewDoors }: BuildCompanyTypeContentOptions = {}
): CompanyTypePageContent {
  const nursinghome = data?.nursinghome

  const operationalStats: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'CCN Number', value: nursinghome?.ccn || 'N/A' },
    {
      label: 'Certified Beds',
      value:
        nursinghome?.number_of_certified_beds != null
          ? formatInteger(nursinghome.number_of_certified_beds)
          : 'N/A',
    },
    {
      label: 'Avg Residents/Day',
      value:
        nursinghome?.average_number_of_residents_per_day != null
          ? formatInteger(nursinghome.average_number_of_residents_per_day)
          : 'N/A',
    },
    {
      label: 'Projected Meals/Day',
      value:
        nursinghome?.projected_meals_per_day != null
          ? formatInteger(nursinghome.projected_meals_per_day)
          : 'N/A',
    },
  ]

  const qualityStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Overall Rating',
      value:
        nursinghome?.overall_rating != null ? (
          <Rating value={nursinghome.overall_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'QM Rating',
      value:
        nursinghome?.qm_rating != null ? (
          <Rating value={nursinghome.qm_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Health Inspection Rating',
      value:
        nursinghome?.health_inspection_rating != null ? (
          <Rating value={nursinghome.health_inspection_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Staffing Rating',
      value:
        nursinghome?.staffing_rating != null ? (
          <Rating value={nursinghome.staffing_rating} />
        ) : (
          'N/A'
        ),
    },
    {
      label: 'Long Stay QM Rating',
      value:
        nursinghome?.long_stay_qm_rating != null ? (
          <Rating value={nursinghome.long_stay_qm_rating} />
        ) : (
          'N/A'
        ),
    },
  ]

  const organizationStats: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'Ownership Type', value: nursinghome?.ownership_type || 'N/A' },
    { label: 'Provider Type', value: nursinghome?.provider_type || 'N/A' },
    {
      label: 'Legal Business Name',
      value: nursinghome?.legal_business_name || 'N/A',
    },
    {
      label: 'Affiliated Entity Name',
      value: nursinghome?.affiliated_entity_name || 'N/A',
    },
    {
      label: 'Continuing Care Retirement Community',
      value: nursinghome?.continuing_care_retirement_community || 'N/A',
    },
  ]

  const summaryContent: React.ReactNode[] = []

  summaryContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      phone={nursinghome?.telephone_number ?? undefined}
      googlePlaceUrl={nursinghome?.google_place_url || undefined}
      address={
        formatAddress({
          street: nursinghome?.address,
          city: nursinghome?.city,
          state: nursinghome?.state,
          zip: nursinghome?.zip_code,
        }) ?? undefined
      }
    />
  )

  if (onViewDoors && data?.doors_count && data.doors_count > 0) {
    summaryContent.push(
      <MetricCard
        key="doors"
        value={data.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={onViewDoors}
      />
    )
  }

  summaryContent.push(
    <StatsCard key="operational-stats" stats={operationalStats} />,
    <Divider key="divider-1" />,
    <StatsCard key="quality-stats" stats={qualityStats} />,
    <Divider key="divider-2" />,
    <StatsCard key="organization-stats" stats={organizationStats} />
  )

  const nursingHomeLocation = nursinghome?.address
    ? `${nursinghome.city}, ${nursinghome.state} - USA`
    : 'N/A'

  const locationInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: <GoogleMapsLink address={nursinghome?.address} />,
    },
    { label: 'ZIP Code', value: nursinghome?.zip_code || 'N/A' },
    { label: 'State', value: nursinghome?.state || 'N/A' },
    { label: 'City', value: nursinghome?.city || 'N/A' },
    { label: 'Website', value: nursinghome?.website_url || 'N/A' },
    {
      label: 'Date First Approved',
      value: nursinghome?.date_first_approved
        ? new Date(nursinghome.date_first_approved).toLocaleDateString()
        : 'N/A',
    },
  ]

  const locationDetailsContent: React.ReactNode[] = [
    <MetricCard
      key="nursinghome-location"
      value={nursingHomeLocation}
      label="Senior Living Location"
      variant="full-width"
    />,
    <StatsCard key="location-info" stats={locationInfoStats} />,
  ]

  const legacyAddressInfo = toLegacyAddressInfo(nursinghome?.address_info)
  if (legacyAddressInfo) {
    locationDetailsContent.push(
      <Divider key="divider-3" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={legacyAddressInfo}
      />
    )
  }

  return {
    documentTitle: `${nursinghome?.provider_name || 'Not Found'} (Senior Living) - First Bite`,
    header: {
      name: nursinghome?.provider_name ?? '',
      domain: nursinghome?.domain || undefined,
      logoUrl: undefined,
    },
    contactCompanyId: data?.id,
    doorsCount: data?.doors_count ?? 0,
    summaryContent,
    locationDetailsContent,
    breadcrumbRoot: 'Healthcare - Senior Living',
    breadcrumbTitle: 'Senior Living',
    categoryInfo: {
      categorySlug: 'non-commercial',
      subCategorySlug: 'healthcare-nursinghomes',
    },
    companyTypeSlug: 'healthcare-nursinghomes',
    tableTypeSlug: 'healthcare-nursinghomes',
    modalSubCategory: {
      name: 'Healthcare - Senior Living',
      slug: 'healthcare-nursinghomes',
      description: 'Healthcare - Senior Living',
    },
  }
}
